<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import isFunction from 'lodash/isFunction';
import MaskedInput from 'vue-text-mask';
import flatPickr from 'vue-flatpickr-component';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Layout from '@layouts/main';
import User from '@models/User';
import BlockingContainer from '@components/BlockingContainer';
import FileWrapper from '@components/FileWrapper';
import FileDownloadLink from '@components/FileDownloadLink';
import SingleImageCrop from '@components/SingleImageCrop';
import editFormChange from '@mixins/editFormChange';
import beforeLeaveAfterEdit from '@mixins/beforeLeaveAfterEdit';
import datePickerUtility from '@mixins/datePickerUtility';

export default {
  name: 'ProfileIndex',
  components: {
    Layout,
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
    MaskedInput,
    FileWrapper,
    SingleImageCrop,
    FileDownloadLink,
  },
  mixins: [editFormChange, beforeLeaveAfterEdit, datePickerUtility],
  metaInfo() {
    return {
      title: this.$t('titles.profile'),
    };
  },
  data() {
    return {
      password: {
        current_password: null,
        password: null,
        password_confirmation: null,
      },
      user: {
        first_name: null,
        last_name: null,
        sex: 1,
        dob: null,
        mobile_number: null,
        street: null,
        postal_code: null,
        town: null,
        country: null,
        nationality: null,
        bank_name: null,
        iban: null,
        bic: null,
        id_card: null,
        driving_license: null,
        transport_license: null,
        id_card_number: null,
        driving_license_number: null,
        transport_license_date: null,
        avatar: {
          id: Math.random(),
          original: null,
          meta_data: {
            height: 0,
            scaleX: 1,
            scaleY: 1,
            width: 0,
            x: 0,
            y: 0,
          },
        },
      },
      avatarUploadShow: false,
    };
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.auth.user,
      dateConfig: (state) => state.common.dateConfig,
      dOBDateConfig: (state) =>
              Object.assign({}, state.common.dateConfig, {
                maxDate: DateTime.local()
                        .minus({ years: 18 })
                        .toFormat('yyyy-LL-dd'),
              }),
      defaultDateConfig: (state) => state.common.dateConfig,
    }),
  },
  watch: {
    currentUser() {
      this.user = Object.assign({}, this.user, cloneDeep(this.currentUser));
      this.$nextTick(() => {
        this.setUnchanged();
      });
    },
  },
  mounted() {
    this.user = Object.assign({}, this.user, cloneDeep(this.currentUser));
    this.$nextTick(() => {
      this.initBeforeLeave(false, this.updateProfile);
      this.initChangeWatcher(['user']);
    });
  },
  methods: {
    create() {
      this.$validator.validateAll('password').then((result) => {
        if (result) {
          this.$wait.start('password');
          new User({
            id: 'password',
            ...this.password,
          })
            .save()
            .then(() => {
              this.$wait.end('password');
            })
            .catch(() => this.$wait.end('password'));
        }
      });
    },
    updateProfile(callback) {
      this.$validator.validateAll('profile').then((result) => {
        if (result) {
          this.$wait.start('profile');
          new User(
            Object.assign({}, this.user, {
              id: 'profile',
              id_card: Array.isArray(this.user.id_card) ? this.user.id_card[0] : this.user.id_card,
              driving_license: Array.isArray(this.user.driving_license) ? this.user.driving_license[0] : this.user.driving_license,
              transport_license: Array.isArray(this.user.transport_license) ? this.user.transport_license[0] : this.user.transport_license,
            })
          )
            .save()
            .then((response) => {
              this.$store.commit('auth/USER', response.user);
              this.setUnchanged();
              this.avatarUploadShow = false;
              if (callback && isFunction(callback)) callback();
              this.$wait.end('profile');
            })
            .catch(() => this.$wait.end('profile'));
        }
      });
    },
    metaUpdateImage(entity, meta) {
      this.$set(this.user[entity], 'meta_data', meta);
    },
  },
};
</script>

<template>
  <Layout>
    <b-row>
      <b-col sm="12" md="6" lg="6">
        <b-card
          :border-variant="!errors.any('profile') ? 'dark' : 'danger'"
          :header="$t('titles.update_profile')"
          header-tag="header"
          :header-bg-variant="!errors.any('profile') ? 'dark' : 'danger'"
          header-text-variant="white"
        >
          <BlockingContainer v-if="$wait.is('profile')"></BlockingContainer>
          <b-form data-vv-scope="profile" @submit.prevent="updateProfile">
            <b-form-row>
              <b-col sm="6">
                <b-form-group :label="`${$t('fields.first_name')}:`">
                  <b-form-input
                    id="first_name"
                    v-model="user.first_name"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="first_name"
                    :data-vv-as="$t('fields.first_name')"
                    type="text"
                    :state="errors.has('first_name', 'profile') ? false : null"
                    aria-describedby="input-feedback-first_name"
                    :placeholder="$t('placeholders.first_name')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-first_name">
                    <template v-show="errors.has('first_name', 'profile')">{{ errors.first('first_name', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group :label="`${$t('fields.last_name')}:`">
                  <b-form-input
                    id="last_name"
                    v-model="user.last_name"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="last_name"
                    :data-vv-as="$t('fields.last_name')"
                    type="text"
                    :state="errors.has('last_name', 'profile') ? false : null"
                    aria-describedby="input-feedback-last_name"
                    :placeholder="$t('placeholders.last_name')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-last_name">
                    <template v-show="errors.has('last_name', 'profile')">{{ errors.first('last_name', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col sm="6">
                <SingleImageCrop
                  v-if="avatarUploadShow"
                  v-model="user.avatar.original"
                  :meta-data="user.avatar.meta_data"
                  :init="true"
                  :clear-button-container="$style.clearButtonContainer"
                  dropzone-class="fileUploadZoneSmall"
                  image-resize-height="155px"
                  clear-button="btn-sm"
                  @meta-update="metaUpdateImage('avatar', ...arguments)"
                ></SingleImageCrop>
                <template v-else>
                  <img
                    v-if="!user.avatar.original"
                    class="img-thumbnail mx-auto d-block rounded-circle"
                    :class="$style.avatarImg"
                    src="@assets/images/none.png"
                    alt="Avatar"
                    @click="avatarUploadShow = true"
                  />
                  <img
                    v-else
                    :key="user.avatar.large"
                    class="img-thumbnail mx-auto d-block rounded-circle"
                    :class="$style.avatarImg"
                    :src="user.avatar.large"
                    alt="Avatar"
                    @click="avatarUploadShow = true"
                  />
                </template>
              </b-col>
            </b-form-row>

            <b-form-group>
              <b-form-radio-group
                v-model="user.sex"
                v-validate="'required'"
                data-vv-value-path="localValue"
                data-vv-name="sex"
                :data-vv-as="$t('fields.gender')"
                aria-describedby="input-feedback-sex"
              >
                <b-form-radio :value="1">{{ $t('fields.sex.1') }}</b-form-radio>
                <b-form-radio :value="2">{{ $t('fields.sex.2') }}</b-form-radio>
                <b-form-radio :value="3">{{ $t('fields.sex.3') }}</b-form-radio>
              </b-form-radio-group>
              <b-form-invalid-feedback id="input-feedback-sex">
                <template v-show="errors.has('sex', 'profile')">{{ errors.first('sex', 'profile') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.dob')}:`">
              <flat-pickr
                v-model="user.dob"
                v-validate="'required'"
                data-vv-name="dob"
                :data-vv-as="$t('fields.dob')"
                aria-describedby="input-feedback-dob"
                :config="dOBDateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('dob', 'profile') }"
                :placeholder="$t('placeholders.date')"
                @on-close="closeCalendar('user.dob', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-dob">
                <template v-show="errors.has('dob', 'profile')"
                  >{{ errors.first('dob', 'profile') }}
                </template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.mobile_number')}:`">
              <b-form-input
                v-model="user.mobile_number"
                v-validate="'required|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="mobile_number"
                :data-vv-as="$t('fields.mobile_number')"
                type="text"
                :state="errors.has('mobile_number', 'profile') ? false : null"
                aria-describedby="input-feedback-mobile_number"
                :placeholder="$t('placeholders.mobile_number')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-mobile_number">
                <template v-show="errors.has('mobile_number', 'profile')">{{ errors.first('mobile_number', 'profile') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-row>
              <b-col>
                <b-form-group :label="`${$t('fields.street')}:`">
                  <b-form-input
                    id="street"
                    v-model="user.street"
                    v-validate="'required|max:65000'"
                    data-vv-value-path="localValue"
                    data-vv-name="street"
                    :data-vv-as="$t('fields.street')"
                    type="text"
                    :state="errors.has('street', 'profile') ? false : null"
                    aria-describedby="input-feedback-street"
                    :placeholder="$t('placeholders.street')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-street">
                    <template v-show="errors.has('street', 'profile')">{{ errors.first('street', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="`${$t('fields.postal_code')}:`">
                  <b-form-input
                    id="postal_code"
                    v-model="user.postal_code"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="postal_code"
                    :data-vv-as="$t('fields.postal_code')"
                    type="text"
                    :state="errors.has('postal_code', 'profile') ? false : null"
                    aria-describedby="input-feedback-postal_code"
                    :placeholder="$t('placeholders.postal_code')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-postal_code">
                    <template v-show="errors.has('postal_code', 'profile')">{{ errors.first('postal_code', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col>
                <b-form-group :label="`${$t('fields.town')}:`">
                  <b-form-input
                    id="town"
                    v-model="user.town"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="town"
                    :data-vv-as="$t('fields.town')"
                    type="text"
                    :state="errors.has('town', 'profile') ? false : null"
                    aria-describedby="input-feedback-town"
                    :placeholder="$t('placeholders.town')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-town">
                    <template v-show="errors.has('town', 'profile')">{{ errors.first('town', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group :label="`${$t('fields.country')}:`">
                  <b-form-input
                    id="country"
                    v-model="user.country"
                    v-validate="'required|max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="country"
                    :data-vv-as="$t('fields.country')"
                    type="text"
                    :state="errors.has('country', 'profile') ? false : null"
                    aria-describedby="input-feedback-country"
                    :placeholder="$t('placeholders.country')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-country">
                    <template v-show="errors.has('country', 'profile')">{{ errors.first('country', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-group :label="`${$t('fields.nationality')}:`">
              <b-form-input
                id="nationality"
                v-model="user.nationality"
                v-validate="'required|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="nationality"
                :data-vv-as="$t('fields.nationality')"
                type="text"
                :state="errors.has('nationality', 'profile') ? false : null"
                aria-describedby="input-feedback-nationality"
                :placeholder="$t('placeholders.nationality')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-nationality">
                <template v-show="errors.has('nationality', 'profile')">{{ errors.first('nationality', 'profile') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.id_card_number') }}:
                <span class="near-label-info text-secondary" @click.stop>
                  <FontAwesomeIcon id="idCardNumberPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                  <b-popover target="idCardNumberPopover" triggers="focus" placement="rightbottom">
                    {{ $t('infos.id_card_number') }}
                  </b-popover>
                </span>
              </template>
              <b-form-input
                v-model="user.id_card_number"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="id_card_number"
                :data-vv-as="$t('fields.id_card_number')"
                type="text"
                :state="errors.has('id_card_number', 'profile') ? false : null"
                aria-describedby="input-feedback-id_card_number"
                :placeholder="$t('placeholders.id_card_number')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-id_card_number">
                <template v-show="errors.has('id_card_number', 'profile')">{{ errors.first('id_card_number', 'profile') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.id_card')}:`">
              <FileDownloadLink v-if="user.id_card && !Array.isArray(user.id_card)" :file="user.id_card" @remove="user.id_card = null" />
              <FileWrapper
                v-else
                v-model="user.id_card"
                v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
                :data-vv-as="$t('fields.id_card')"
                :state="errors.has('id_card', 'profile') ? false : null"
                :placeholder="$t('placeholders.id_card')"
                accept="image/*,application/pdf"
                aria-describedby="input-feedback-id_card"
                name="id_card"
              >
              </FileWrapper>

              <div v-show="errors.has('id_card', 'profile')" class="invalid-feedback d-block mt-1">
                {{ errors.first('id_card', 'profile') }}
              </div>
            </b-form-group>

            <b-form-group :label="`${$t('fields.driving_license_number')}:`">
              <b-form-input
                v-model="user.driving_license_number"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="driving_license_number"
                :data-vv-as="$t('fields.driving_license_number')"
                type="text"
                :state="errors.has('driving_license_number', 'profile') ? false : null"
                aria-describedby="input-feedback-driving_license_number"
                :placeholder="$t('placeholders.driving_license_number')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-driving_license_number">
                <template v-show="errors.has('driving_license_number', 'profile')">{{ errors.first('driving_license_number', 'profile') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.driving_license')}:`">
              <FileDownloadLink
                v-if="user.driving_license && !Array.isArray(user.driving_license)"
                :file="user.driving_license"
                @remove="user.driving_license = null"
              />
              <FileWrapper
                v-else
                v-model="user.driving_license"
                v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
                :data-vv-as="$t('fields.driving_license')"
                :state="errors.has('driving_license', 'profile') ? false : null"
                :placeholder="$t('placeholders.driving_license')"
                accept="image/*,application/pdf"
                aria-describedby="input-feedback-driving_license"
                name="driving_license"
              >
              </FileWrapper>

              <div v-show="errors.has('driving_license', 'profile')" class="invalid-feedback d-block mt-1">
                {{ errors.first('driving_license', 'profile') }}
              </div>
            </b-form-group>

            <b-form-group :label="`${$t('fields.transport_license_date')}:`">
              <flat-pickr
                      v-model="user.transport_license_date"
                      v-validate="''"
                      data-vv-name="transport_license_date"
                      :data-vv-as="$t('fields.transport_license_date')"
                      aria-describedby="input-feedback-transport_license_date"
                      :config="defaultDateConfig"
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('transport_license_date', 'profile') }"
                      :placeholder="$t('placeholders.transport_license_date')"
                      @on-close="closeCalendar('user.transport_license_date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-transport_license_date">
                <template v-show="errors.has('transport_license_date', 'profile')"
                >{{ errors.first('transport_license_date', 'profile') }}
                </template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.transport_license')}:`">
              <FileDownloadLink
                v-if="user.transport_license && !Array.isArray(user.transport_license)"
                :file="user.transport_license"
                @remove="user.transport_license = null"
              />
              <FileWrapper
                v-else
                v-model="user.transport_license"
                v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
                :data-vv-as="$t('fields.transport_license')"
                :state="errors.has('transport_license', 'profile') ? false : null"
                :placeholder="$t('placeholders.transport_license')"
                accept="image/*,application/pdf"
                aria-describedby="input-feedback-transport_license"
                name="transport_license"
              >
              </FileWrapper>

              <div v-show="errors.has('transport_license', 'profile')" class="invalid-feedback d-block mt-1">
                {{ errors.first('transport_license', 'profile') }}
              </div>
            </b-form-group>

            <b-form-group :label="`${$t('fields.bank_name')}:`">
              <b-form-input
                id="bank_name"
                v-model="user.bank_name"
                v-validate="'max:255'"
                data-vv-value-path="localValue"
                data-vv-name="bank_name"
                :data-vv-as="$t('fields.bank_name')"
                type="text"
                :state="errors.has('bank_name', 'profile') ? false : null"
                aria-describedby="input-feedback-bank_name"
                :placeholder="$t('placeholders.bank_name')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-bank_name">
                <template v-show="errors.has('bank_name', 'profile')">{{ errors.first('bank_name', 'profile') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-row>
              <b-col sm="7">
                <b-form-group :label="`${$t('fields.iban')}:`">
                  <MaskedInput
                    v-model="user.iban"
                    v-validate="'max:27'"
                    data-vv-value-path="localValue"
                    data-vv-name="iban"
                    :data-vv-as="$t('fields.iban')"
                    type="text"
                    name="phone"
                    class="form-control"
                    :mask="[
                      /[A-Z]/i,
                      /[A-Z]/i,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                      /\d/,
                      /\d/,
                      ' ',
                      /\d/,
                      /\d/,
                    ]"
                    :guide="true"
                    :pipe="(conformedValue) => conformedValue.toUpperCase()"
                    placeholder-char="_"
                    aria-describedby="input-feedback-iban"
                    :class="errors.has('iban', 'profile') ? 'is-invalid' : null"
                    placeholder="DE12 3456 7890 1234 5678 91"
                  >
                  </MaskedInput>

                  <b-form-invalid-feedback id="input-feedback-iban">
                    <template v-show="errors.has('iban', 'profile')">{{ errors.first('iban', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group>
                  <template v-slot:label class="col-form-legend col-12">
                    {{ $t('fields.bic') }}:
                    <span class="near-label-info text-secondary" @click.stop>
                      <FontAwesomeIcon id="bicPopover" :icon="['fas', 'info-circle']" />
                      <b-popover target="bicPopover" triggers="focus" placement="rightbottom">
                        {{ $t('infos.bic') }}
                      </b-popover>
                    </span>
                  </template>
                  <b-form-input
                    id="bic"
                    v-model="user.bic"
                    v-validate="'max:255'"
                    data-vv-value-path="localValue"
                    data-vv-name="bic"
                    :data-vv-as="$t('fields.bic')"
                    type="text"
                    :state="errors.has('bic', 'profile') ? false : null"
                    aria-describedby="input-feedback-bic"
                    :placeholder="$t('placeholders.bic')"
                  >
                  </b-form-input>

                  <b-form-invalid-feedback id="input-feedback-bic">
                    <template v-show="errors.has('bic', 'profile')">{{ errors.first('bic', 'profile') }}</template>
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-button type="submit" :disabled="errors.any('profile')" :variant="!errors.any('profile') ? 'outline-dark' : 'outline-danger'">{{
              $t('actions.submit')
            }}</b-button>
          </b-form>
        </b-card>
      </b-col>
      <b-col sm="12" md="6" lg="6">
        <b-card
          :border-variant="!errors.any('password') ? 'dark' : 'danger'"
          :header="$t('titles.update_password')"
          header-tag="header"
          :header-bg-variant="!errors.any('password') ? 'dark' : 'danger'"
          header-text-variant="white"
          class="mb-3"
        >
          <BlockingContainer v-if="$wait.is('password')"></BlockingContainer>
          <b-form data-vv-scope="password" @submit.prevent="create">
            <b-form-group :label="`${$t('fields.current_password')}:`">
              <b-form-input
                id="current_password"
                v-model="password.current_password"
                v-validate="'required|min:6|max:255'"
                data-vv-value-path="localValue"
                data-vv-name="current_password"
                :data-vv-as="$t('fields.current_password')"
                type="password"
                name="current_password"
                :state="errors.has('password.current_password') ? false : null"
                aria-describedby="input-feedback-current_password"
                :placeholder="$t('placeholders.current_password')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-current_password">
                <template v-show="errors.has('password.current_password')">{{ errors.first('password.current_password') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.new_password')}:`">
              <b-form-input
                id="password"
                ref="password"
                v-model="password.password"
                v-validate="'required|min:8|max:255|verify_password'"
                data-vv-value-path="localValue"
                data-vv-name="password"
                :data-vv-as="$t('fields.new_password')"
                name="password"
                type="password"
                :state="errors.has('password.password') ? false : null"
                aria-describedby="input-feedback-password"
                :placeholder="$t('placeholders.password')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-password">
                <template v-show="errors.has('password.password')">{{ errors.first('password.password') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group :label="`${$t('fields.new_password_repeat')}:`">
              <b-form-input
                v-model="password.password_confirmation"
                v-validate="'required|confirmed:password'"
                data-vv-value-path="localValue"
                data-vv-name="password_confirmation"
                :data-vv-as="$t('fields.repeat_password')"
                type="password"
                name="password_confirmation"
                :state="errors.has('password.password_confirmation') ? false : null"
                aria-describedby="input-feedback-password_confirmation"
                :placeholder="$t('placeholders.new_password_repeat')"
              >
              </b-form-input>

              <b-form-invalid-feedback id="input-feedback-password_confirmation">
                <template v-show="errors.has('password.password_confirmation')">{{ errors.first('password.password_confirmation') }}</template>
              </b-form-invalid-feedback>
            </b-form-group>

            <b-button type="submit" :disabled="errors.any('password')" :variant="!errors.any('password') ? 'outline-dark' : 'outline-danger'">{{
              $t('actions.submit')
            }}</b-button>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>
<style scoped>
  .custom-file  {
    white-space: nowrap!important;
  }
</style>
<style lang="scss" module>
@import '@design';

.avatarImg {
  height: 160px;
  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
